import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, MenuItem, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ComprobanteDialog from "./ComprobanteDialog";
import { fieldSx } from "./formStyles";
import { exportarReporteExcel } from "./exportarReporteExcel";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AsientoDialog from "./AsientoDialog";
import {
  buscarClientesEgresos, ejecutarReporte, fmtFecha, fmtMoney, getCatalogosReportes,
} from "../services/egresosService";

const cellSx = { py: 0.2, px: 0.5, fontSize: 11 };
const VERDE = "success.main";

const hoy = () => new Date().toISOString().slice(0, 10);
const inicioAnio = () => `${new Date().getFullYear()}-01-01`;
const anios = () => {
  const actual = new Date().getFullYear();
  return Array.from({ length: actual - 2014 }, (_, i) => actual - i);
};

const COLOR_BALANCE = { azul: "info.main", verde: "success.main", ocre: "warning.main", total: "primary.main" };

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const COLUMNAS_EGRESO = [
  ["Fecha", "fecha"], ["N° egreso", "id"], ["N° cheque", "nocheque"], ["Concepto", "concepto"],
  ["Depósito a", "deposito"], ["Macrocuenta", "macrocuenta"], ["Contrato", "nro_contrato"],
  ["Referencia", "nro_referencia"], ["Factura", "nro_factura"], ["Servicio", "servicio"], ["Banco", "banco"],
  ["Mes", "mes"], ["Dirigido a", "nombre"], ["Ciudad", "ciudad"], ["Bs", "bolivianos"], ["$us", "dolares"],
  ["Nulo", "anulado"], ["Observación", "obs"], ["Cliente", "razon_social"],
];

const Sel = ({ label, value, onChange, opciones, ancho = 200 }) => (
  <TextField select size="small" label={label || undefined} value={value} onChange={onChange} sx={{ ...fieldSx, width: ancho }}>
    {opciones.map((o) => <MenuItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</MenuItem>)}
  </TextField>
);
const Libre = ({ label, value, opciones, onChange, ancho = 240 }) => (
  <Autocomplete size="small" options={opciones} value={value || null} onChange={(_, v) => onChange(v || "")}
    sx={{ width: ancho }} renderInput={(params) => <TextField {...params} label={label || undefined} sx={fieldSx} />} />
);

// Colores que cambian con el tema: tono(claro, oscuro) se usa dentro de sx.
const tono = (claro, oscuro) => (t) => (t.palette.mode === "dark" ? oscuro : claro);
const CELESTE = tono("#bde3ec", "#1b4b57");
const AMARILLO = tono("#ffecb8", "#4d4220");
const VERDE_CL = tono("#c5e6c9", "#20472a");
const ROJO = tono("#e00000", "#ff6b6b");
const AZUL_TIT = tono("#1976d2", "#6cb2ff");
const BOTON_BG = tono("#f1f1f1", "#2d2d2d");

// Botón "ver comprobante" de cada fila: un <button> nativo sin efecto hover ni ripple, para que la tabla grande pese menos.
const BOTON_VER = { background: "none", border: 0, padding: 2, margin: 0, cursor: "pointer", color: "#1976d2", display: "inline-flex", fontSize: 16, lineHeight: 1 };

const R = ({ children }) => <Box component="span" sx={{ color: ROJO }}>{children}</Box>;
const Campo = ({ value, onChange }) => (
  <TextField size="small" value={value} onChange={onChange} sx={{ ...fieldSx, width: 150, verticalAlign: "middle" }} />
);
const botonLegacy = { py: 0, px: 1, minHeight: 24, fontSize: 12, color: "text.primary", bgcolor: BOTON_BG, borderColor: "text.disabled", borderRadius: 0, textTransform: "none", whiteSpace: "nowrap" };
// Fila del formulario viejo: etiqueta (con sus campos) a la izquierda y botón(es) a la derecha.
const Fila = ({ bg, etiqueta, acciones, disabled }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", bgcolor: bg, borderTop: "1px solid", borderColor: "divider", fontSize: 12.5, "& .MuiSelect-select": { fontSize: "0.78rem" } }}>
    <Box sx={{ px: 1, py: 0.3, display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }}>{etiqueta}</Box>
    <Box sx={{ px: 0.75, py: 0.3, display: "flex", gap: 0.5, borderLeft: "1px solid", borderColor: "divider", minWidth: 96, justifyContent: "center" }}>
      {acciones.map(([etq, fn]) => (
        <Button key={etq} size="small" variant="outlined" disabled={disabled} onClick={fn} sx={botonLegacy}>{etq}</Button>
      ))}
    </Box>
  </Box>
);

// Reportes de Caja General y de Balance. Cada pantalla replica la del sistema viejo
// (co_reportes_cg/balance.php e index.php): una fila por reporte con su botón de consulta.
export default function ReportesCajaGeneralTab({ modo = "caja" }) {
  const [cat, setCat] = useState(null);
  const [errorCat, setErrorCat] = useState("");
  const [fein, setFein] = useState(inicioAnio());
  const [fefin, setFefin] = useState(hoy());
  const anioActual = new Date().getFullYear();
  const [f, setF] = useState({
    ciudad: "La Paz", numero: "", contrato: "", referencia: "", banco: "", banco_year: anioActual, banco_credito: "",
    nombre: "", concepto: "", macrocuenta: "", macro_mes: new Date().getMonth() + 1, macro_year: anioActual, area: "",
    year: anioActual, mes: new Date().getMonth() + 1, balance: "",
  });
  const [cliente, setCliente] = useState(null);
  const [opcionesCliente, setOpcionesCliente] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [filtro, setFiltro] = useState("");
  const [comprobanteId, setComprobanteId] = useState(null);
  const [previo, setPrevio] = useState(null);
  const [asientoOpen, setAsientoOpen] = useState(false);
  const [orden, setOrden] = useState({ k: null, dir: 1 });

  useEffect(() => {
    getCatalogosReportes()
      .then((res) => {
        const d = res.data;
        setCat(d);
        setF((p) => ({
          ...p, banco: d.bancos[0] || "", banco_credito: (d.bancos_credito[0] || d.bancos[0]) || "", area: d.areas[0] || "",
          concepto: d.conceptos[0] || "", macrocuenta: d.macrocuentas[0]?.id ?? "", balance: d.cuentas?.[0]?.cuenta ?? "",
        }));
      })
      .catch(() => setErrorCat("No se pudieron cargar los datos del formulario."));
  }, []);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const buscarClientes = (texto) => {
    if (texto.trim().length < 2) { setOpcionesCliente([]); return; }
    buscarClientesEgresos(texto.trim()).then((res) => setOpcionesCliente(res.data || [])).catch(() => setOpcionesCliente([]));
  };

  const correr = async (params) => {
    setOrden({ k: null, dir: 1 });
    setCargando(true);
    setError("");
    setFiltro("");
    try {
      const { data } = await ejecutarReporte(params);
      setPrevio(null);
      setResultado(data);
    } catch (e) {
      setResultado(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setCargando(false);
    }
  };

  const fechas = { fein, fefin };
  const verBalance = (t) => correr({ tipo: t, balance: f.balance, mes: f.mes, year: f.year });
  // Botón de una fila de Caja General: valida lo que el reporte exige y lo lanza.
  const consultar = (params, falta) => {
    if (falta) { setError(falta); setResultado(null); return; }
    correr(params);
  };

  const verDetalleMacro = async (id, mes, year) => {
    setCargando(true);
    setError("");
    try {
      const { data } = await ejecutarReporte({ tipo: "macrocuenta", macrocuenta: id, mes, year });
      setPrevio(resultado);
      setOrden({ k: null, dir: 1 });
      setFiltro("");
      setResultado(data);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar el detalle.");
    } finally {
      setCargando(false);
    }
  };

  // Orden de las tablas de resultados: números por valor, el resto como texto (con números naturales).
  const ordenar = useCallback((filas) => {
    if (!orden.k) return filas;
    const num = (v) => (v !== null && v !== undefined && String(v).trim() !== "" && Number.isFinite(Number(String(v).replace(/,/g, ""))) ? Number(String(v).replace(/,/g, "")) : null);
    return [...filas].sort((a, b) => {
      const x = a[orden.k];
      const y = b[orden.k];
      if (x === null || x === undefined || x === "") return 1;
      if (y === null || y === undefined || y === "") return -1;
      const nx = num(x);
      const ny = num(y);
      const r = nx !== null && ny !== null ? nx - ny : String(x).localeCompare(String(y), "es", { numeric: true, sensitivity: "base" });
      return r * orden.dir;
    });
  }, [orden]);

  // Celdas de encabezado ordenables: cols = [[etiqueta, campo, alineación]].
  const cab = (cols) => cols.map(([h, k, align]) => (
    <TableCell key={h} align={align} sortDirection={orden.k === k ? (orden.dir === 1 ? "asc" : "desc") : false} sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
      <TableSortLabel active={orden.k === k} direction={orden.dir === 1 ? "asc" : "desc"} onClick={() => setOrden((o) => ({ k, dir: o.k === k ? -o.dir : 1 }))}>{h}</TableSortLabel>
    </TableCell>
  ));

  const items = useMemo(() => {
    if (!resultado?.items) return [];
    const t = filtro.trim().toLowerCase();
    const base = !t ? resultado.items : resultado.items.filter((r) => Object.values(r).some((v) => String(v ?? "").toLowerCase().includes(t)));
    return ordenar(base);
  }, [resultado, filtro, ordenar]);

  if (errorCat) return <Alert severity="error">{errorCat}</Alert>;
  if (!cat) return <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      {modo === "balance" ? (
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden", maxWidth: 900, color: "text.primary" }}>
          <Typography sx={{ textAlign: "center", fontWeight: 900, fontSize: 20, py: 0.5, bgcolor: tono("#7fa5ad", "#2f4d53"), color: AZUL_TIT }}>
            Reporte de <Box component="span" sx={{ color: ROJO }}>Balance</Box>
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", bgcolor: "background.paper", "& > *": { p: 0.5 } }}>
            <TextField select fullWidth size="small" value={f.balance} onChange={set("balance")} sx={fieldSx}>
              {(cat.cuentas || []).map((c) => <MenuItem key={c.cuenta} value={c.cuenta}>{c.concepto}</MenuItem>)}
            </TextField>
            <TextField select fullWidth size="small" value={f.mes} onChange={(e) => setF((p) => ({ ...p, mes: Number(e.target.value) }))} sx={fieldSx}>
              {MESES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
            </TextField>
            <TextField select fullWidth size="small" value={f.year} onChange={set("year")} sx={fieldSx}>
              {anios().map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
          </Box>
          {[
            { titulo: "Egresos :", bg: tono("#dcd88a", "#4d4a20"), cols: 3, items: [["Por mes de Cuenta", "cuenta_mes"], ["Por Año de Cuenta", "cuenta_year"], ["Todos los Egresos de Año", "egresos_todo"]] },
            { titulo: "Asientos Contables:", bg: tono("#ccf5cc", "#1f4a2a"), cols: 4, items: [["Insertar :", "__insertar"], ["Ver Todos :", "asientos_todos"], ["Mayores por Cuenta :", "asiento_mayor"], ["Todos los Mayores :", "mayores_todos"]] },
          ].map((g) => (
            <Box key={g.titulo}>
              <Typography sx={{ textAlign: "center", fontWeight: 800, fontSize: 12.5, py: 0.4, bgcolor: g.bg, color: AZUL_TIT, borderTop: "2px solid", borderColor: "background.paper" }}>{g.titulo}</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${g.cols}, 1fr)`, bgcolor: g.bg, gap: "2px" }}>
                {g.items.map(([etq, t]) => (
                  <Box key={t} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 0.6, bgcolor: g.bg, fontSize: 12.5 }}>
                    {etq}
                    <Button size="small" variant="outlined" disabled={cargando}
                      onClick={() => (t === "__insertar" ? setAsientoOpen(true) : verBalance(t))}
                      sx={{ minWidth: 0, py: 0, px: 0.8, fontWeight: 800, color: AZUL_TIT, bgcolor: BOTON_BG, borderColor: "text.disabled", textTransform: "none" }}>
                      Ver
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr 2fr", bgcolor: tono("#ffb97a", "#5a3a1c"), gap: "2px", borderTop: "2px solid", borderColor: "background.paper" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, bgcolor: tono("#ffb97a", "#5a3a1c") }}>Balance :</Box>
            {[["Ver Balance de Activos", "balance_activo"], ["Ver Balance Pasivos", "balance_pasivo"], ["Ver Estado de Resultados", "estado_resultados"]].map(([etq, t]) => (
              <Box key={t} sx={{ display: "flex", justifyContent: "center", py: 0.6, bgcolor: tono("#ffb97a", "#5a3a1c") }}>
                <Button size="small" variant="outlined" disabled={cargando} onClick={() => verBalance(t)}
                  sx={{ py: 0, px: 1, fontWeight: 800, color: AZUL_TIT, bgcolor: BOTON_BG, borderColor: "text.disabled", textTransform: "none" }}>
                  {etq}
                </Button>
              </Box>
            ))}
          </Box>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden", maxWidth: 1080, mx: "auto", borderRadius: 0, color: "text.primary" }}>
          <Typography sx={{ textAlign: "center", fontWeight: 900, fontSize: 16, py: 0.4, bgcolor: VERDE_CL }}>
            Reporte de <Box component="span" sx={{ color: ROJO }}>Caja General</Box>
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, flexWrap: "wrap", py: 0.5, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", fontWeight: 700, fontSize: 12 }}>
            Fecha Inicio:
            <TextField size="small" type="date" value={fein} onChange={(e) => setFein(e.target.value)} sx={{ ...fieldSx, width: 150 }} />
            Fecha Fin:
            <TextField size="small" type="date" value={fefin} onChange={(e) => setFefin(e.target.value)} sx={{ ...fieldSx, width: 150 }} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "start" }}>
          <Box>
          <Fila bg="background.paper" etiqueta={<>Reporte de <R>Egreso de Cheques</R></>} disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "egreso", ...fechas })]]} />
          <Fila bg="background.paper" etiqueta={<>Cheques Emitidos en la Ciudad de <Sel label="" value={f.ciudad} onChange={set("ciudad")} opciones={cat.ciudades} ancho={140} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "ciudad", ciudad: f.ciudad, ...fechas })]]} />
          <Fila bg="background.paper" etiqueta={<>Revizar <R>Cheques por Número</R> <Campo value={f.numero} onChange={set("numero")} /></>} disabled={cargando}
            acciones={[["Consultar", () => consultar({ tipo: "numero", numero: f.numero.trim(), sw: 0 }, !f.numero.trim() && "Ingresa el número de cheque")]]} />
          <Fila bg="background.paper" etiqueta={<>Número de Contrato <Campo value={f.contrato} onChange={set("contrato")} /></>} disabled={cargando}
            acciones={[["Consultar", () => consultar({ tipo: "numero", numero: f.contrato.trim(), sw: 1 }, !f.contrato.trim() && "Ingresa el número de contrato")]]} />
          <Fila bg="background.paper" etiqueta={<>Número de Referencia <Campo value={f.referencia} onChange={set("referencia")} /></>} disabled={cargando}
            acciones={[["Consultar", () => consultar({ tipo: "numero", numero: f.referencia.trim(), sw: 2 }, !f.referencia.trim() && "Ingresa el número de referencia")]]} />
          <Fila bg="background.paper" etiqueta={<>Reporte <R>de Cheques Anulados</R></>} disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "anulado", ...fechas })]]} />
          <Fila bg="background.paper" etiqueta={<>Reporte de <R>Bancos</R> <Sel label="" value={f.banco} onChange={set("banco")} opciones={cat.bancos} ancho={170} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "banco", banco: f.banco, ...fechas })]]} />
          <Fila bg="background.paper" etiqueta={<>Reporte de Anual <Sel label="" value={f.banco_year} onChange={set("banco_year")} opciones={anios()} ancho={100} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "banco_anual", year: f.banco_year })]]} />
          <Fila bg={CELESTE} etiqueta={<>Reporte de <R>Traspaso de Dinero</R></>} disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "traspaso", ...fechas })]]} />
          <Fila bg={CELESTE} etiqueta={<>Reporte de <R>Credito Bancario</R> <Sel label="" value={f.banco_credito} onChange={set("banco_credito")} opciones={cat.bancos_credito.length ? cat.bancos_credito : cat.bancos} ancho={170} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "credito", banco: f.banco_credito, ...fechas })]]} />
          </Box>
          <Box sx={{ borderLeft: { md: "1px solid" }, borderColor: "divider" }}>
          <Fila bg={AMARILLO} etiqueta={<>Emitido a: <Libre label="" value={f.nombre} opciones={cat.nombres} onChange={(v) => setF((p) => ({ ...p, nombre: v }))} ancho={240} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "emitido", nombre: f.nombre, ...fechas }, !f.nombre && "Selecciona el nombre (Emitido a)")]]} />
          <Fila bg={AMARILLO} etiqueta={<>Concepto : <Sel label="" value={f.concepto} onChange={set("concepto")} opciones={cat.conceptos} ancho={300} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "concepto", concepto: f.concepto, ...fechas })]]} />
          <Fila bg={AMARILLO} etiqueta={<>Ver con <R>ambas selecciones</R> Nombre - Concepto</>} disabled={cargando}
            acciones={[["Consultar", () => consultar({ tipo: "ambos", nombre: f.nombre, concepto: f.concepto, ...fechas }, (!f.nombre || !f.concepto) && "Selecciona el nombre (Emitido a) y el concepto")]]} />
          <Fila bg={AMARILLO} etiqueta={<>Macrocuenta : <Sel label="" value={f.macrocuenta} onChange={set("macrocuenta")} opciones={cat.macrocuentas.map((m) => ({ value: m.id, label: m.nombre }))} ancho={200} /></>}
            disabled={cargando} acciones={[["Consultar", () => consultar({ tipo: "macrocuenta", macrocuenta: f.macrocuenta, ...fechas }, f.macrocuenta === "" && "Selecciona la macrocuenta")]]} />
          <Fila bg={AMARILLO} etiqueta={<>Macrocuenta por Mes: <Sel label="" value={f.macro_mes} onChange={(e) => setF((p) => ({ ...p, macro_mes: Number(e.target.value) }))} opciones={MESES.map((m, i) => ({ value: i + 1, label: m }))} ancho={120} /> <Sel label="" value={f.macro_year} onChange={set("macro_year")} opciones={anios()} ancho={90} /></>}
            disabled={cargando} acciones={[
              ["Consultar", () => consultar({ tipo: "macrocuenta_mes", mes: f.macro_mes, year: f.macro_year })],
              ["Mostrar Año", () => consultar({ tipo: "macrocuenta_year", year: f.macro_year })],
            ]} />
          <Fila bg={AMARILLO} etiqueta={<>Área <Sel label="" value={f.area} onChange={set("area")} opciones={cat.areas} ancho={170} /></>}
            disabled={cargando} acciones={[
              ["Consultar", () => consultar({ tipo: "area", area: f.area, ...fechas })],
              ["Resumen", () => consultar({ tipo: "resumen", ...fechas })],
            ]} />
          <Box sx={{ bgcolor: VERDE_CL, px: 1, py: 0.5, borderTop: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 800, fontSize: 12, color: tono("#0a6a6a", "#7fd1d1"), whiteSpace: "nowrap" }}>Egresos por Servicios del Cliente:</Typography>
            <Autocomplete size="small" options={opcionesCliente} value={cliente} filterOptions={(x) => x} sx={{ flex: 1, minWidth: 180 }}
              getOptionLabel={(o) => `${o.razon_social} ${o.servicio} ${o.descripcion || ""} (${o.id})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onInputChange={(_, v, reason) => { if (reason === "input") buscarClientes(v); }}
              onChange={(_, v) => setCliente(v)} noOptionsText="Escribe para buscar un cliente"
              renderInput={(params) => <TextField {...params} placeholder="Cliente" sx={fieldSx} />} />
            <Button size="small" variant="contained" disabled={cargando} sx={{ ...botonLegacy, color: "#fff", bgcolor: "primary.main", borderColor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              onClick={() => consultar({ tipo: "servicio", servicio: cliente?.id || "", ...fechas }, !cliente && "Selecciona el cliente / servicio")}>
              Consultar
            </Button>
          </Box>
          </Box>
          </Box>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

      {!resultado && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>Elige un reporte y presiona su botón de consulta.</Typography>
      )}

      {resultado && (
        <Paper variant="outlined" sx={{
          overflow: "hidden", borderRadius: 0,
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: "rgba(14,107,107,0.13)", color: tono("#0b4f4f", "#bfe9e9"), borderBottom: "2px solid #0e6b6b", fontWeight: 800,
            "& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active": { color: "inherit" },
            "& .MuiTableSortLabel-icon": { color: "inherit !important" },
          },
          "& .MuiTableBody-root tr:nth-of-type(even)": { bgcolor: "action.hover" },
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.6, bgcolor: "#0e6b6b", color: "#fff", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: 0.4 }}>
              {resultado.titulo}{resultado.total !== undefined ? ` — ${resultado.total} resultado(s)` : ""}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {previo && <Button size="small" onClick={() => { setResultado(previo); setPrevio(null); }} sx={{ minHeight: 24, height: 24, fontSize: 11, borderRadius: 0, color: "#fff", border: "1px solid rgba(255,255,255,0.7)" }}>← Volver al resumen</Button>}
            <Button size="small" startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />} onClick={() => exportarReporteExcel(resultado, items)}
              sx={{ minHeight: 24, height: 24, fontSize: 11, borderRadius: 0, color: "#fff", border: "1px solid rgba(255,255,255,0.7)", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}>
              Exportar a Excel
            </Button>
            {resultado.tipo === "egresos" && (
              <>
                <Typography sx={{ fontSize: 11, color: "#a5d6a7", fontWeight: 700 }}>Verde = emitido en dólares</Typography>
                <TextField size="small" placeholder="Filtrar resultados..." value={filtro} onChange={(e) => setFiltro(e.target.value)}
                  sx={{ ...fieldSx, width: 220 }} />
              </>
            )}
          </Box>

          {resultado.tipo === "egresos" && (
            <>
              <TableContainer>
                <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                  <TableHead>
                    <TableRow>
                      {cab(COLUMNAS_EGRESO.map(([h, k]) => [h, k, h === "Bs" || h === "$us" ? "right" : "left"]))}
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!items.length ? (
                      <TableRow><TableCell colSpan={20}><Typography variant="body2" color="text.secondary">No existen registros.</Typography></TableCell></TableRow>
                    ) : items.map((r) => (
                      <TableRow key={r.id} hover sx={r.moneda !== "Bolivianos" ? { "& .MuiTableCell-root": { color: VERDE } } : undefined}>
                        {COLUMNAS_EGRESO.map(([h, k]) => (
                          <TableCell key={k} align={k === "bolivianos" || k === "dolares" ? "right" : "left"}
                            sx={{ whiteSpace: k === "obs" ? "normal" : "nowrap", minWidth: k === "obs" ? 140 : undefined,
                              ...(k === "bolivianos" ? { fontWeight: 700, color: "#1565c0" } : k === "dolares" ? { fontWeight: 700, color: "#2e7d32" } : k === "id" ? { fontWeight: 800, color: tono("#0e6b6b", "#7fd1d1") } : {}) }}>
                            {k === "fecha" ? fmtFecha(r.fecha) : (k === "bolivianos" || k === "dolares") ? fmtMoney(r[k]) : r[k]}
                          </TableCell>
                        ))}
                        <TableCell>
                          <button type="button" title="Ver comprobante" aria-label="Ver comprobante" style={BOTON_VER} onClick={() => setComprobanteId(r.id)}>
                            <VisibilityIcon fontSize="inherit" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: tono("#0e6b6b", "#7fd1d1"), mb: 0.5 }}>Resumen de importes</Typography>
                <Table size="small" sx={{ maxWidth: 640, "& .MuiTableCell-root": { py: 0.3, px: 1, fontSize: 12 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Moneda de emisión</TableCell>
                      <TableCell align="right">Cheques</TableCell>
                      <TableCell align="right">Importe emitido</TableCell>
                      <TableCell align="right">Equivale a Bs</TableCell>
                      <TableCell align="right">Equivale a $us</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[["Bolivianos", "Bolivianos", "Bs", "bolivianos"], ["Dólares", "Dolares", "$us", "dolares"]].map(([etq, k, uni, campo]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ fontWeight: 700 }}>Emitidos en {etq}</TableCell>
                        <TableCell align="right">{resultado.por_moneda?.[k]?.cantidad ?? 0}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(resultado.por_moneda?.[k]?.[campo])} {uni}</TableCell>
                        <TableCell align="right" sx={{ color: "#1565c0" }}>{fmtMoney(resultado.por_moneda?.[k]?.bolivianos)}</TableCell>
                        <TableCell align="right" sx={{ color: "#2e7d32" }}>{fmtMoney(resultado.por_moneda?.[k]?.dolares)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: 900 }}>Total general</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{resultado.total}</TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ fontWeight: 900, color: "#1565c0" }}>{fmtMoney(resultado.total_bolivianos)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: "#2e7d32" }}>{fmtMoney(resultado.total_dolares)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Typography sx={{ fontSize: 10.5, mt: 0.5 }} color="text.secondary">
                  Cada cheque se convierte con el tipo de cambio con que se registró: los emitidos en dólares aparecen también en Bs, y los emitidos en Bs también en $us.
                </Typography>
              </Box>
              {resultado.con_resumen_servicio && (
                <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                  <Table size="small" sx={{ "& .MuiTableCell-root": cellSx, maxWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Moneda / Servicio</TableCell>
                        {Object.keys(resultado.por_servicio).map((s) => <TableCell key={s} align="right" sx={{ fontWeight: 800 }}>{s}</TableCell>)}
                        <TableCell align="right" sx={{ fontWeight: 800 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[["Bolivianos", "bolivianos"], ["Dólares", "dolares"]].map(([etq, k]) => (
                        <TableRow key={k}>
                          <TableCell sx={{ fontWeight: 700 }}>{etq}</TableCell>
                          {Object.values(resultado.por_servicio).map((v, i) => <TableCell key={i} align="right">{fmtMoney(v[k])}</TableCell>)}
                          <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(Object.values(resultado.por_servicio).reduce((s, v) => s + v[k], 0))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {resultado.tipo === "macro_mes" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx, maxWidth: 560 }}>
                <TableHead><TableRow>{cab([["Macrocuenta", "macrocuenta"], ["Bolivianos", "bolivianos", "right"], ["Dólares", "dolares", "right"]])}</TableRow></TableHead>
                <TableBody>
                  {ordenar(resultado.filas).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.macrocuenta}</TableCell>
                      {["bolivianos", "dolares"].map((k) => (
                        <TableCell key={k} align="right">
                          {r[k] ? <Box component="a" href="#detalle" onClick={(e) => { e.preventDefault(); verDetalleMacro(r.id, resultado.mes, resultado.year); }} sx={{ color: "primary.main", cursor: "pointer" }}>{fmtMoney(r[k])}</Box> : fmtMoney(r[k])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  <TableRow><TableCell sx={{ fontWeight: 800 }}>Total</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_bolivianos)}</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_dolares)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "macro_year" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Macrocuenta</TableCell><TableCell sx={{ fontWeight: 800 }}>Moneda</TableCell>
                    {resultado.meses.map((m) => <TableCell key={m} align="right" sx={{ fontWeight: 800 }}>{m.slice(0, 3)}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultado.filas.flatMap((r) => [["Bs.", "bolivianos"], ["$us.", "dolares"]].map(([etq, k], j) => (
                    <TableRow key={`${r.id}-${k}`} hover>
                      {j === 0 && <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>{r.macrocuenta}</TableCell>}
                      <TableCell>{etq}</TableCell>
                      {r[k].map((v, i) => (
                        <TableCell key={i} align="right">
                          {v ? <Box component="a" href="#detalle" onClick={(e) => { e.preventDefault(); verDetalleMacro(r.id, i + 1, resultado.year); }} sx={{ color: "primary.main", cursor: "pointer" }}>{fmtMoney(v)}</Box> : fmtMoney(v)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )))}
                  {[["Bs.", "total_bolivianos"], ["$us.", "total_dolares"]].map(([etq, k], j) => (
                    <TableRow key={k} sx={{ bgcolor: "action.hover" }}>
                      {j === 0 && <TableCell rowSpan={2} sx={{ fontWeight: 800 }}>Total</TableCell>}
                      <TableCell sx={{ fontWeight: 700 }}>{etq}</TableCell>
                      {resultado[k].map((v, i) => <TableCell key={i} align="right" sx={{ fontWeight: 800 }}>{fmtMoney(v)}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "balance" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx, maxWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Cuenta</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Bolivianos</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>Dólares</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultado.lineas.map((l, i) => {
                    const c = COLOR_BALANCE[l.color] || "text.primary";
                    if (l.nivel === "seccion") {
                      return <TableRow key={i}><TableCell colSpan={3} sx={{ fontWeight: 800, color: c, borderBottom: 2, borderColor: c }}>{l.nombre}</TableCell></TableRow>;
                    }
                    const negrita = l.nivel !== "detalle";
                    return (
                      <TableRow key={i} sx={l.nivel === "total" ? { bgcolor: "action.hover" } : undefined}>
                        <TableCell sx={{ pl: l.nivel === "detalle" ? 2 : 0.5, fontWeight: negrita ? 800 : 400, color: negrita ? c : undefined }}>{l.nombre}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: negrita ? 800 : 400 }}>{fmtMoney(l.bs)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: negrita ? 800 : 400 }}>{fmtMoney(l.usd)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "cuentas_monto" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx, maxWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    {cab([["N° cuenta", "cuenta"], ["Nombre de la cuenta", "nombre"], ["Bolivianos", "bolivianos", "right"], ["Dólares", "dolares", "right"]])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenar(resultado.filas).map((r) => (
                    <TableRow key={r.cuenta} hover>
                      <TableCell>{r.cuenta}</TableCell><TableCell>{r.nombre}</TableCell>
                      <TableCell align="right">{fmtMoney(r.bolivianos)}</TableCell><TableCell align="right">{fmtMoney(r.dolares)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_bolivianos)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_dolares)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "asientos" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {cab([["Código", "id"], ["Fecha", "fecha"], ["Cuenta 1 (debe)", "cuenta1"], ["Cuenta 2 (haber)", "cuenta2"], ["Bolivianos", "bolivianos", "right"], ["Dólares", "dolares", "right"], ["Observación", "observacion"]])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenar(resultado.items).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell><TableCell>{fmtFecha(r.fecha)}</TableCell><TableCell>{r.cuenta1}</TableCell><TableCell>{r.cuenta2}</TableCell>
                      <TableCell align="right">{fmtMoney(r.bolivianos)}</TableCell><TableCell align="right">{fmtMoney(r.dolares)}</TableCell><TableCell>{r.observacion}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_bolivianos)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_dolares)}</TableCell><TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "mayor" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {cab([["Código", "id"], ["Fecha", "fecha"], ["Debe Bs", "debe_bs", "right"], ["Debe $us", "debe_usd", "right"], ["Haber Bs", "haber_bs", "right"], ["Haber $us", "haber_usd", "right"], ["Contra cuenta", "contra"], ["Observación", "observacion"]])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenar(resultado.items).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell><TableCell>{fmtFecha(r.fecha)}</TableCell>
                      {["debe_bs", "debe_usd", "haber_bs", "haber_usd"].map((k) => <TableCell key={k} align="right">{r[k] === null ? "" : fmtMoney(r[k])}</TableCell>)}
                      <TableCell>{r.contra}</TableCell><TableCell>{r.observacion}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Totales</TableCell>
                    {["debe_bs", "debe_usd", "haber_bs", "haber_usd"].map((k) => <TableCell key={k} align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado[k])}</TableCell>)}
                    <TableCell colSpan={2} sx={{ fontWeight: 800 }}>Saldo: Bs {fmtMoney(resultado.saldo_bs)} · $us {fmtMoney(resultado.saldo_usd)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "cuenta_year" && (
            <Box sx={{ p: 1, display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
              <TableContainer sx={{ width: "auto" }}>
                <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                  <TableHead><TableRow><TableCell /><TableCell align="right" sx={{ fontWeight: 800 }}>Bolivianos</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>Dólares</TableCell></TableRow></TableHead>
                  <TableBody>
                    {resultado.meses.map((m, i) => (
                      <TableRow key={m}><TableCell sx={{ fontWeight: 700 }}>{m}</TableCell><TableCell align="right">{fmtMoney(resultado.bolivianos[i])}</TableCell><TableCell align="right">{fmtMoney(resultado.dolares[i])}</TableCell></TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: "action.hover" }}><TableCell sx={{ fontWeight: 800 }}>Totales</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_bolivianos)}</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_dolares)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <TableContainer sx={{ width: "auto" }}>
                <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                  <TableHead>
                    <TableRow><TableCell rowSpan={2} sx={{ fontWeight: 800 }}>Banco</TableCell><TableCell colSpan={2} align="center" sx={{ fontWeight: 800 }}>Emitido en Bs</TableCell><TableCell colSpan={2} align="center" sx={{ fontWeight: 800 }}>Emitido en $us</TableCell></TableRow>
                    <TableRow>{["Bs.", "$us.", "Bs.", "$us."].map((h, i) => <TableCell key={i} align="right" sx={{ fontWeight: 700 }}>{h}</TableCell>)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(resultado.bancos).map(([b, v]) => (
                      <TableRow key={b}>
                        <TableCell sx={{ fontWeight: 700 }}>{b}</TableCell>
                        {[v.en_bs_bs, v.en_bs_usd, v.en_usd_bs, v.en_usd_usd].map((x, i) => <TableCell key={i} align="right">{fmtMoney(x)}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {resultado.tipo === "resumen" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx, maxWidth: 520 }}>
                <TableHead><TableRow><TableCell sx={{ fontWeight: 800 }}>Egresos en</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>Total en dólares americanos</TableCell></TableRow></TableHead>
                <TableBody>
                  {resultado.filas.map((r) => (
                    <TableRow key={r.area}><TableCell>{r.area}</TableCell><TableCell align="right">{fmtMoney(r.dolares)}</TableCell></TableRow>
                  ))}
                  <TableRow><TableCell sx={{ fontWeight: 800 }}>Total</TableCell><TableCell align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.total_dolares)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "traspasos" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {cab([["ID", "id"], ["Banco origen", "banco_origen"], ["Comprobante origen", "comprobante_origen"], ["Cheque origen", "num_cheque_origen"], ["Banco destino", "banco_destino"], ["Comprobante destino", "comprobante_destino"], ["Cheque destino", "num_cheque_destino"], ["Dólares", "dolares", "right"], ["Bolivianos", "bolivianos", "right"], ["Fecha", "fecha"]])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!resultado.items.length ? <TableRow><TableCell colSpan={10}>No existen registros.</TableCell></TableRow> : ordenar(resultado.items).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell><TableCell>{r.banco_origen}</TableCell><TableCell>{r.comprobante_origen}</TableCell>
                      <TableCell>{r.num_cheque_origen}</TableCell><TableCell>{r.banco_destino}</TableCell><TableCell>{r.comprobante_destino}</TableCell>
                      <TableCell>{r.num_cheque_destino}</TableCell><TableCell align="right">{fmtMoney(r.dolares)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.bolivianos)}</TableCell><TableCell>{fmtFecha(r.fecha)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "creditos" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    {cab([["ID", "id"], ["Banco destino", "banco_destino"], ["Bolivianos", "bolivianos", "right"], ["Dólares", "dolares", "right"], ["Moneda", "moneda"], ["Plazo", "plazo"], ["Comprobante", "comprobante"], ["Garantía", "garantia"], ["Fecha", "fecha"], ["Registrado por", "usu"]])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!resultado.items.length ? <TableRow><TableCell colSpan={10}>No existen registros.</TableCell></TableRow> : ordenar(resultado.items).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell><TableCell>{r.banco_destino}</TableCell>
                      <TableCell align="right">{fmtMoney(r.bolivianos)}</TableCell><TableCell align="right">{fmtMoney(r.dolares)}</TableCell>
                      <TableCell>{r.moneda}</TableCell><TableCell>{r.plazo}</TableCell><TableCell>{r.comprobante}</TableCell>
                      <TableCell>{r.garantia}</TableCell><TableCell>{fmtFecha(r.fecha)}</TableCell><TableCell>{r.usu}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {resultado.tipo === "banco_anual" && (
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} />
                    {resultado.bancos.map((b) => <TableCell key={b} colSpan={2} align="center" sx={{ fontWeight: 800 }}>{b.replace(/_/g, " ")}</TableCell>)}
                  </TableRow>
                  <TableRow>
                    {resultado.bancos.flatMap((b) => [
                      <TableCell key={`${b}-bs`} align="right" sx={{ fontWeight: 700 }}>Bs.</TableCell>,
                      <TableCell key={`${b}-us`} align="right" sx={{ fontWeight: 700 }}>$us.</TableCell>,
                    ])}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultado.meses.map((mes, i) => (
                    <TableRow key={mes} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{mes}</TableCell>
                      {resultado.bancos.flatMap((b) => [
                        <TableCell key={`${b}-bs`} align="right">{fmtMoney(resultado.montos[b].bolivianos[i])}</TableCell>,
                        <TableCell key={`${b}-us`} align="right">{fmtMoney(resultado.montos[b].dolares[i])}</TableCell>,
                      ])}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                    {resultado.bancos.flatMap((b) => [
                      <TableCell key={`${b}-bs`} align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.montos[b].total_bolivianos)}</TableCell>,
                      <TableCell key={`${b}-us`} align="right" sx={{ fontWeight: 800 }}>{fmtMoney(resultado.montos[b].total_dolares)}</TableCell>,
                    ])}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {modo === "balance" && <AsientoDialog open={asientoOpen} onClose={() => setAsientoOpen(false)} cuentas={cat.cuentas || []} />}
      <ComprobanteDialog id={comprobanteId} onClose={() => setComprobanteId(null)} />
    </Box>
  );
}
